"use client"

import { Upload, X } from "lucide-react"
import type React from "react"
import { useState } from "react"
import { Button } from "#/components/ui/button"
import { Card } from "#/components/ui/card"
import { Input } from "#/components/ui/input"
import { Label } from "#/components/ui/label"
import { Textarea } from "#/components/ui/textarea"
import { authClient } from "#/lib/auth/client"

interface VideoPostFormProps {
  onPostCreated: () => void
}

export function VideoPostForm({ onPostCreated }: VideoPostFormProps) {
  const [caption, setCaption] = useState("")
  const [videoPreview, setVideoPreview] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const { session } = authClient.useSession()

  const handleVideoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const url = URL.createObjectURL(file)
      setVideoPreview(url)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!videoPreview || !session) return

    setIsUploading(true)

    // Mock upload delay
    await new Promise(resolve => setTimeout(resolve, 3000))

    // In a real app, you would upload the video and create the post
    console.log("Creating video post:", { caption, videoPreview })

    setIsUploading(false)
    onPostCreated()
  }

  const removeVideo = () => {
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview)
      setVideoPreview(null)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <Label>Video</Label>
        {!videoPreview ? (
          <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
            <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-sm text-muted-foreground mb-4">
              Upload a video to share
            </p>
            <Input
              type="file"
              accept="video/*"
              onChange={handleVideoUpload}
              className="max-w-xs mx-auto"
            />
          </div>
        ) : (
          <Card className="relative">
            <video
              src={videoPreview}
              className="w-full h-64 object-cover rounded-lg"
              controls
            />
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="absolute top-2 right-2"
              onClick={removeVideo}
            >
              <X className="h-4 w-4" />
            </Button>
          </Card>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="caption">Caption</Label>
        <Textarea
          id="caption"
          placeholder="Write a caption..."
          value={caption}
          onChange={e => setCaption(e.target.value)}
          rows={3}
        />
      </div>

      <Button
        type="submit"
        className="w-full"
        disabled={!videoPreview || isUploading}
      >
        {isUploading ? "Uploading..." : "Share Video"}
      </Button>
    </form>
  )
}
